import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import _ from "lodash";
import {
  updateBasicInformation,
  sendChatLineMessage,
  sendFreeChatLineMessage
} from 'api/core';
import { ChangeReceptionsField, ChatInputField } from './components';
import { BasicInformation } from './components/BasicInformation';
import { PersonalInformation } from './components/PersonalInformation';
import { LockInformation } from './components/LockInformation';
import { OperationHistories } from './components/OperationHistories';
import { currentUserSelector } from "store/current_user";
import { useHistory, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import {
  basicInformationInputValidationSchema,
  createChatInputValidationSchema,
  freeChatInputValidationSchema,
} from 'utils/formik/validationSchema';
import {
  basicInformationInputInitialValues,
  chatInputInitialValues,
  freeChatInputInitialValues,
} from 'utils/formik/initialValues';
import { createBasicInformationInputErrorMessage } from 'utils/validationMessage';
import { getApplyIdFromQuery } from 'utils/query';
import AsyncButton from 'utils/AsyncButton';
import { ErrorList } from 'components';
import styles from 'styles/admin-detail.module.scss';
import MaybeLoading from 'utils/MaybeLoading';
import CircularProgress from '@material-ui/core/CircularProgress';
import Scroll, { scroller } from 'react-scroll';
import PictureAsPdf from '@material-ui/icons/PictureAsPdf';
import CloseIcon from '@material-ui/icons/Close';
import { AppBar, Toolbar } from '@material-ui/core';
import {
  DEFAULT_ERROR_MESSAGE,
  UPDATE_SUCCESS_MESSAGE,
  SEND_SUCCESS_MESSAGE,
} from 'const';
import { Card, CardHeader } from '@material-ui/core';
import { pdfExport } from 'api/pdfExport';
import { fetchApplyDetails } from 'api/detail/core';
import { getStylesBackgroundColor} from './components';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    height: '100%',
  },
}));

const Detail = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const currentUser = useSelector(currentUserSelector);
  const currentUserId = currentUser !== null ? currentUser.userId : null;
  const beforeUnloadListener = (event) => {
    event.preventDefault();
    event.returnValue = '';
  }
  const id = getApplyIdFromQuery(location.search) || '';
  const [loading, setLoading] = useState(false);
  const [detailData, setDetailData] = useState({});

  const [
    shouldDisplayBasicInformationErrorMessage,
    setShouldDisplayBasicInformationErrorMessage,
  ] = useState(false);
  const [
    isBasicInformationSubmitInProgress,
    setIsBasicInformationSubmitInProgress,
  ] = useState(false);
  const basicInformationInputFormik = useFormik({
    initialValues: basicInformationInputInitialValues,
    validationSchema: basicInformationInputValidationSchema,
    onSubmit: (values) => {
      if (!values.callFlg) {
        values.callDesignatedDate = null;
        values.callDesignatedTimeAndMemo = null;
      } else {
        values.callDesignatedTimeAndMemo =
          values.callDesignatedTimeAndMemo || '';
      }
      setIsBasicInformationSubmitInProgress(true);
      updateBasicInformation(String(detailData.basicInfo?.applyId), values).then(
        (resp) => {
          setIsBasicInformationSubmitInProgress(false);
          if (resp.status === 'success') {
            alert(UPDATE_SUCCESS_MESSAGE);
            window.onbeforeunload = null
            history.go(0);
          } else {
            alert(DEFAULT_ERROR_MESSAGE);
          }
        }
      );
    },
  });

  const [isChatSubmitInProgress, setIsChatSubmitInProgress] = useState(
    false
  );
  const chatInputFormik = useFormik({
    initialValues: chatInputInitialValues,
    validationSchema: createChatInputValidationSchema(!!detailData?.vehicle?.id, !!detailData?.nenrei?.id),
    onSubmit: (values) => {
      setIsChatSubmitInProgress(true);
      const postData = { ...values, ...detailData.chatInput, applyId: String(detailData.basicInfo?.applyId) }
      delete postData.notificationId
      delete postData.sendLineAt
      delete postData.type
      delete postData.responseStatus
      sendChatLineMessage(postData).then((resp) => {
        setIsChatSubmitInProgress(false);
        if (resp.status === 'success') {
          // テストモードの場合は、レスポンスで帰ってくる承諾URLのパスパラメータの値をアラートで出す。
          const message = Number(process.env.REACT_APP_TEST_MODE_FLAG) !== 1 ? SEND_SUCCESS_MESSAGE : resp.data["url_path_param"]
          alert(message);
          window.onbeforeunload = null
          history.go(0);
        } else if (resp.data.data) {
          // 表示すべきエラーメッセージがあればアラート表示する
          alert(resp.data.data);
        } else {
          alert(DEFAULT_ERROR_MESSAGE);
        }
      });
    },
  });

  const [isFreeChatSubmitInProgress, setIsFreeChatSubmitInProgress] = useState(
    false
  );
  const freeChatInputFormik = useFormik({
    initialValues: freeChatInputInitialValues,
    validationSchema: freeChatInputValidationSchema,
    onSubmit: (values) => {
      setIsFreeChatSubmitInProgress(true);
      sendFreeChatLineMessage(String(detailData.basicInfo?.applyId), values).then((resp) => {
        setIsFreeChatSubmitInProgress(false);
        if (resp.status === 'success') {
          // テストモード、かつ2: フリーメッセージ以外の場合は、レスポンスで帰ってくる承諾URLのパスパラメータの値をアラートで出す。
          const message = (Number(process.env.REACT_APP_TEST_MODE_FLAG) !== 1 || values.type === 2) ? SEND_SUCCESS_MESSAGE : resp.data["url_path_param"]
          alert(message);
          window.onbeforeunload = null
          history.go(0);
        } else {
          alert(DEFAULT_ERROR_MESSAGE);
        }
      });
    },
  });
  const scrollToBasicInformationInputErrorMessage = () => {
    scroller.scrollTo('basic_information_input_error_message', {
      duration: 800,
      delay: 0,
      smooth: 'easeInOutQuart',
      offset: -70,
    });
  };

  // 通話履歴 項目名の変換用メソッド
  const convertCaseStyleOfCallHistories = (outboundsLists) => {
    let callHistories = [];
    for (let i = 0; i < outboundsLists?.length; i++) {
      callHistories.push({
        id: outboundsLists[i].outbound_id,
        outboundDate: outboundsLists[i].outbound_date,
        memo: outboundsLists[i].outbound_memo ? outboundsLists[i].outbound_memo : "",
      });
    }
    return callHistories;
  };

  // LINEチャット履歴 項目名の変換用メソッド
  const convertCaseStyleOfChatHistories = (notificationsList) => {
    let histories = [];
    for (let i = 0; i < notificationsList?.length; i++) {
      histories.push(
        notificationsList[i].type === 1
          ? convertCaseStyleOfNotifications(notificationsList[i], true)
          : convertCaseStyleOfNotificationsFreeChat(notificationsList[i])
      );
    }
    return histories;
  };

  // チャット履歴の変数名を変換する
  const convertCaseStyleOfNotifications = (
    notifications,
    isChatHistory,
    carInsuranceType,
    carInsuranceRequest
  ) => {
    let convertedNotifications = {
      notificationId: notifications.notification_id,
      messageType: notifications.message_type,
      message: notifications.message,
      currentContractCarName: notifications.current_contract_car_name,
      currentContractCarRegistrationNumber:
        notifications.current_contract_car_registration_number,
      isLongTermContract: notifications.is_long_term_contract,
      updatedContractCarName: notifications.updated_contract_car_name,
      yearAndMonthOfFirstRegistration:
        notifications.year_and_month_of_first_registration,
      carModel: notifications.car_model,
      chassisNumber: notifications.chassis_number,
      carType: notifications.car_type,
      baseLocationForUse: notifications.base_location_for_use,
      carOwner: notifications.car_owner,
      carOwnerKana: notifications.car_owner_kana,
      outOfPocketFeeFirstTime: notifications.out_of_pocket_fee_first_time,
      outOfPocketFeeAfterSecondTime:
        notifications.out_of_pocket_fee_after_second_time,
      updatedContractCarRegistrationNumber:
        notifications.updated_contract_car_registration_number,
      updatedCarInsuranceFee: notifications.updated_car_insurance_fee,
      carInsuranceFeeNextYear: notifications.car_insurance_fee_next_year,
      carInsuranceFeeThirdYear: notifications.car_insurance_fee_third_year,
      isPersonalAndInsuranceStartTerm:
        notifications.is_personal_and_insurance_start_term,
      updatedDriverSpecialContract:
        notifications.updated_driver_special_contract,
      updatedDriverAgeConditionSpecialContract:
        notifications.updated_driver_age_condition_special_contract,
      driverLicenseColorOfYoungest:
        notifications.driver_license_color_of_youngest,
      genderOfYoungest: notifications.gender_of_youngest,
      afterCurrentPurpose: notifications.after_current_purpose,
      forBusinessUseCar: notifications.for_business_use_car,
      addOrMinus: notifications.add_or_minus,
      updatedInsuranceFee: notifications.updated_insurance_fee,
      updatedMonthlyInsuranceFee: notifications.updated_monthly_insurance_fee,
      aboutUpdatedInsuranceFee: notifications.about_updated_insurance_fee,
      aboutUpdatedInsuranceFeeDetail:
        notifications.about_updated_insurance_fee_detail,
      updatedInsuranceFeeOfNextYear:
        notifications.updated_insurance_fee_of_next_year,
      paymentMethodOfNextYear: notifications.payment_method_of_next_year,
      updatedInsuranceFeeOfThirdYear:
        notifications.updated_insurance_fee_of_third_year,
      paymentMethodOfThirdYear: notifications.payment_method_of_third_year,
      newCarSpecialContractAddOrMinus:
        notifications.new_car_special_contract_add_or_minus,
      newCarSpecialContractUpdatedInsuranceFee:
        notifications.new_car_special_contract_updated_insurance_fee,
      newCarSpecialContractUpdatedMonthlyInsuranceFee:
        notifications.new_car_special_contract_updated_monthly_insurance_fee,
      newCarSpecialContractUpdatedInsuranceFeeOfNextYear:
        notifications.new_car_special_contract_updated_insurance_fee_of_next_year,
      newCarSpecialContractPaymentMethodOfNextYear:
        notifications.new_car_special_contract_payment_method_of_next_year,
      newCarSpecialContractUpdatedInsuranceFeeOfThirdYear:
        notifications.new_car_special_contract_updated_insurance_fee_of_third_year,
      newCarSpecialContractPaymentMethodOfThirdYear:
        notifications.new_car_special_contract_payment_method_of_third_year,
      remarks: notifications.remarks,
      sendLineAt: notifications.send_line_at,
      type: notifications.type,
      carInsuranceRequest: carInsuranceRequest,
      carInsuranceType: carInsuranceType,
    };
    // チャット履歴であれば、以下を追加して返却
    if (isChatHistory) {
      convertedNotifications['carInsuranceType'] =
        notifications.car_insurance_type;
      convertedNotifications['answeredAt'] = notifications.answered_at;
      convertedNotifications['acceptanceStatus'] =
        notifications.acceptance_status;
      convertedNotifications['receptionDatetime'] =
        notifications.reception_datetime;
      convertedNotifications['policyNumber'] = notifications.policy_number;
      convertedNotifications['detailNumber'] = notifications.detail_number;
      convertedNotifications['name'] = notifications.name;
      convertedNotifications['driverLicenseColorOfYoungest'] =
        notifications.driver_license_color_of_youngest;
      convertedNotifications['genderOfYoungest'] =
        notifications.gender_of_youngest;
      convertedNotifications['afterCurrentPurpose'] =
        notifications.after_current_purpose;
      convertedNotifications['forBusinessUseCar'] =
        notifications.for_business_use_car;
      convertedNotifications['changeDate'] = notifications.change_date;
      convertedNotifications['hasPassed14DaysSincePublishedUrl'] =
        notifications.has_passed_14_days_since_published_url;
      convertedNotifications['urlInvalidFlg'] =
        notifications.url_invalid_flg;
      convertedNotifications['callPreferredTime'] =
        notifications.call_preferred_time;
      convertedNotifications['updatedInsuranceFeePlan'] =
        notifications.updated_insurance_fee_plan;
      convertedNotifications['furtherQuestions'] =
        notifications.further_questions;
    }
    return convertedNotifications;
  };
  // チャット履歴（フリーメッセージ）の変数名を変換する
  const convertCaseStyleOfNotificationsFreeChat = (notifications) => {
    return {
      notificationId: notifications.notification_id,
      answeredAt: notifications.answered_at,
      acceptanceStatus: notifications.acceptance_status,
      type: notifications.type,
      messageType: notifications.message_type,
      freeMessage: notifications.free_message,
      message: notifications.message,
      sendLineAt: notifications.send_line_at,
      answerWithFreeText: notifications.answer_with_free_text,
      vehicleInspectionCertImgPath: notifications.vehicle_inspection_cert_img_path,
      furtherQuestions: notifications.further_questions,
      hasPassed14DaysSincePublishedUrl: notifications.has_passed_14_days_since_published_url,
      urlInvalidFlg: notifications.url_invalid_flg
    };
  };

  useEffect(() => {
    getDetailData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentUserId && detailData.lockedBy === currentUserId) {
      // 自分でロックしている場合は、画面を閉じる際にアラートを出す
      window.onbeforeunload = beforeUnloadListener
      return () => {
        window.onbeforeunload = null
      };
    }
  }, [currentUserId, detailData.lockedBy]);

  // 詳細取得APIを呼び出し、各表示項目とformikに値をセット
  const getDetailData = async () => {
    setLoading(true);
    const result = await fetchApplyDetails(id);
    if (Object.keys(result).length === 0) {
      history.push(`/not-found`);
      return
    }

    setDetailPageDisplayValues(result);
    setLoading(false);
  };

  // 基本情報の入力項目の変更有無
  const [isBasicInformationInputChanged, setIsBasicInformationInputChanged] = useState(false)

  // 基本情報の入力項目（APIから取得した時点・変更前）
  const [
    originalBasicInformationInputValues,
    setOriginalBasicInformationInputValues
  ] = useState(basicInformationInputInitialValues)

  // 基本情報の入力項目の変更有無検知
  useEffect(() => {
    const isChanged = !(_.isEqual(originalBasicInformationInputValues, basicInformationInputFormik.values));
    setIsBasicInformationInputChanged(isChanged);
  }, [originalBasicInformationInputValues, basicInformationInputFormik.values]);

  const setDetailPageDisplayValues = (result) => {

    const basicInformationInput = {
      isSmsSendingRequired: String(result.is_sms_sending_required),
      responseStatus: String(result.response_status),
      memoAD: result.memo_ad ? result.memo_ad : "",
      memoSV: result.memo_sv ? result.memo_sv : "",
      callFlg: result.call_designated_date ? true : false,
      callDesignatedDate: result.call_designated_date,
      callDesignatedTimeAndMemo: result.call_designated_time_and_memo ? result.call_designated_time_and_memo : "",
      isIdentifiedAll: result.is_identified_all ? result.is_identified_all : "",
      nameRevised: result.name_revised ? result.name_revised : "",
      nameKanaRevised: result.name_kana_revised ? result.name_kana_revised : "",
      policyNumberRevised: result.policy_number_revised ? result.policy_number_revised : "",
      memo: result.memo ? result.memo : "",
      callHistories: convertCaseStyleOfCallHistories(result.outbounds_list),
    }

    // formik管理： 基本情報
    basicInformationInputFormik.setValues(basicInformationInput);
    setOriginalBasicInformationInputValues(_.cloneDeep(basicInformationInput));

    // 表示用：詳細画面の情報
    setDetailData({
      lockedBy: result.locked_by,
      userName: result.user_name,
      insuranceType: result.insurance_type,
      feeCategory: result.fee_category,
      cancellationCategory: result.cancellation_category,
      basicInfo: {
        applyId: result.apply_id,
        createdAt: result.created_at,
        updatedAt: result.updated_at,
        lineId: result.line_id,
      },
      personalInfo: {
        inputter: result.inputter,
        isPersonal: result.is_personal,
        policyNumber: result.policy_number,
        policyNumberRevised: result.policy_number_revised,
        detailNumber: result.detail_number,
        name: result.name,
        nameRevised: result.name_revised,
        nameKana: result.name_kana,
        nameKanaRevised: result.name_kana_revised,
        registeredTel1: result.registered_tel_1,
        registeredTel2: result.registered_tel_2,
        registeredTel3: result.registered_tel_3,
        carNumber: result.car_number,
        birthday: result.birthday,
        zipCode: result.zip_code,
        prefecture: result.prefecture,
        city: result.city,
        chome: result.chome,
        building: result.building,
        contactTel: result.contact_tel,
        email: result.email,
        isIdentifiedAll: result.is_identified_all,
        isIdentifiedSyoken: result.is_identified_syoken,
        isIdentifiedNameKana: result.is_identified_name_kana,
        isIdentifiedTel: result.is_identified_tel,
        deptCode: result.dept_code,
        deptName: result.dept_name,
        agencyCode: result.agency_code,
        agencyName: result.agency_name,
      },
      addressChange: {
        id: result.address_changes_id,
        updateContents: result.update_contents,
        isLocationChange: result.is_location_change,
        isChangeOfInsuredAddress: result.is_change_of_insured_address,
        newZipCode: result.new_zip_code,
        newPrefecture: result.new_prefecture,
        newPrefectureKana: result.new_prefecture_kana,
        newCity: result.new_city,
        newCityKana: result.new_city_kana,
        newChome: result.new_chome,
        newChomeKana: result.new_chome_kana,
        newBuilding: result.new_building,
        newBuildingKana: result.new_building_kana,
        newTel1: result.address_change_new_tel_1,
        newTel2: result.address_change_new_tel_2,
        newTel3: result.address_change_new_tel_3,
        deleteTelFlg: result.address_change_delete_tel_flg,
        newMobileTel1: result.address_change_new_mobile_tel_1,
        newMobileTel2: result.address_change_new_mobile_tel_2,
        newMobileTel3: result.address_change_new_mobile_tel_3,
        deleteMobileTelFlg: result.address_change_delete_mobile_tel_flg,
        insuredTel1: result.insured_tel_1,
        insuredTel2: result.insured_tel_2,
        insuredTel3: result.insured_tel_3,
      },
      syoken: {
        id: result.syokens_id,
        reissueReason: result.reissue_reason,
      },
      payment: {
        id: result.payments_id,
        newPayment: result.new_payment,
        preferredProcedure: result.preferred_procedure,
      },
      changePrice: {
        changeDate: result.change_date,
      },
      vehicle: {
        id: result.vehicles_id,
        carInsuranceRequest: result.car_insurance_request,
        carInsuranceType: result.car_insurance_type,
        price: result.price,
        s3FileName: result.s3_file_name,
        howToGetCar: result.how_to_get_car,
        isLeaseTermMoreThan1Year: result.is_lease_term_more_than_1_year,
        isCarForWelfare: result.is_car_for_welfare
      },
      nenrei: {
        id: result.nenreis_id,
        relationWithYoungest: result.relation_with_youngest,
        birthdayOfYoungest: result.birthday_of_youngest,
        driverLicenseColorOfYoungest: result.driver_license_color_of_youngest,
        genderOfYoungest: result.gender_of_youngest,
        ageOfYoungest: result.age_of_youngest,
        driverType: result.driver_type,
      },
      purpose: {
        id: result.purposes_id,
        afterCurrentPurpose: result.after_current_purpose,
        forBusinessUseCar: result.for_business_use_car,
      },
      cancellation: {
        id: result.cancellations_id,
        cancellationReason: result.cancellation_reason,
        cancellationReasonText: result.cancellation_reason_text,
        newInsuranceLine: result.new_insurance_line,
        newInsuranceSection: result.new_insurance_section,
        isCancellationForUnify: result.is_cancellation_for_unify,
        isCertificateRequired: result.is_certificate_required,
        cancellationDate: result.cancellation_date,
        estateType: result.estate_type,
        destination: result.destination,
        movingDay: result.moving_day,
        destZipCode: result.dest_zip_code,
        destPrefecture: result.dest_prefecture,
        destPrefectureKana: result.dest_prefecture_kana,
        destCity: result.dest_city,
        destCityKana: result.dest_city_kana,
        destChome: result.dest_chome,
        destChomeKana: result.dest_chome_kana,
        destBuilding: result.dest_building,
        destBuildingKana: result.dest_building_kana,
        deliveryName: result.delivery_name,
        occurrenceDate: result.occurrence_date,
        updateContents: result.cancellations_update_contents,
        newTel1: result.cancel_new_tel_1,
        newTel2: result.cancel_new_tel_2,
        newTel3: result.cancel_new_tel_3,
        deleteTelFlg: result.cancel_delete_tel_flg,
        newMobileTel1: result.cancel_new_mobile_tel_1,
        newMobileTel2: result.cancel_new_mobile_tel_2,
        newMobileTel3: result.cancel_new_mobile_tel_3,
        deleteMobileTelFlg: result.cancel_delete_mobile_tel_flg,
      },
      chatInput: {
        receptionDatetime: result.created_at,
        policyNumber: result.policy_number_revised || result.policy_number,
        detailNumber: result.detail_number,
        name: result.name_revised || result.name,
        driverLicenseColorOfYoungest: result.driver_license_color_of_youngest,
        genderOfYoungest: result.gender_of_youngest,
        afterCurrentPurpose: result.after_current_purpose,
        forBusinessUseCar: result.for_business_use_car,
        changeDate: result.change_date,
        responseStatus: String(result.response_status),
      },
      chatHistories: convertCaseStyleOfChatHistories(result.notifications_list),
    });

    // formik管理：LINEチャットの情報
    // チャット履歴があれば、最新の履歴を編集画面に表示
    let isChatInputFormikEmpty = true;
    let isFreeChatInputFormikEmpty = true;
    for (let i = 0; i < result.notifications_list?.length; i++) {
      if (result.notifications_list[i].type === 1 && isChatInputFormikEmpty) {
        chatInputFormik.setValues(
          convertCaseStyleOfNotifications(
            result.notifications_list[i],
            // LINE履歴用ではなく、formikに詰めるデータを返す
            false,
            // LINEチャットのバリデーションで使用するため、vehiclesテーブルから取得したcar_insurance_typeをセットする
            result.car_insurance_type,
            result.car_insurance_request
          )
        );
        isChatInputFormikEmpty = false;
      }
      if (result.notifications_list[i].type !== 1 && isFreeChatInputFormikEmpty) {
        const {
          type,
          messageType,
          freeMessage,
          message
        } = convertCaseStyleOfNotificationsFreeChat(result.notifications_list[i])
        freeChatInputFormik.setValues({
          type,
          messageType,
          freeMessage,
          message
        });
        isFreeChatInputFormikEmpty = false;
      }
    }
    if (isChatInputFormikEmpty) {
      // chatInputFormikにnotification_contentsの最新情報が入っていない場合、
      // car_insurance_type、car_insurance_requestのみ個別でセットする
      chatInputFormik.setFieldValue('carInsuranceType', result.car_insurance_type)
      chatInputFormik.setFieldValue('carInsuranceRequest', result.car_insurance_request)
    }
  };

  return (
    <div className={classes.root}>
      <div className={styles.root}>
        <div className={styles.goBackContainer}>
          <span
            className={styles.goBack}
            onClick={window.close}
          >
            <CloseIcon />
            タブを閉じる
          </span>
        </div>
        <MaybeLoading
          loading={loading}
          progressComponent={
            <div className={styles.circularProgressContainer}>
              <CircularProgress />
            </div>
          }
        >
          <LockInformation
            applyId={id}
            lockedBy={detailData.lockedBy}
            isBasicInformationInputChanged={isBasicInformationInputChanged}
            setIsBasicInformationInputChanged={setIsBasicInformationInputChanged}
            getDetailData={getDetailData}
            userName={detailData.userName}
          />
          <div className={styles.buttonArea}>
            <div className={styles.updateAreaButton}>
              <AsyncButton
                startIcon={<PictureAsPdf />}
                type="button"
                variant="outlined"
                color="primary"
                onClick={async () => {
                  await pdfExport([detailData.basicInfo?.applyId]);
                }}
              >
                帳票印刷
              </AsyncButton>
            </div>
          </div>
          <CardHeader
            title='工程管理'
            className={getStylesBackgroundColor(detailData.insuranceType)}
          />
          <Card className={styles.cardContent}>
            <Scroll.Element name='basic_information_input_error_message'>
              <ErrorList
                errors={createBasicInformationInputErrorMessage(
                  basicInformationInputFormik.errors
                )}
                isError={shouldDisplayBasicInformationErrorMessage}
              />
            </Scroll.Element>
            {/* 工程管理・本人確認情報・変更内容 */}
            <div className={styles.field}>
              <BasicInformation
                insuranceType={detailData.insuranceType}
                basicInfo={detailData.basicInfo}
                basicInformationInputFormik={basicInformationInputFormik}
                lockedBy={detailData.lockedBy}
              />
            </div>
          </Card>
          <Card>
            <PersonalInformation
              basicInformationInputFormik={basicInformationInputFormik}
              {...detailData}
            />
          </Card>
          <div className={styles.field}>
            <ChangeReceptionsField
              detailData={detailData}
              className={styles.field}
            />
          </div>
          <OperationHistories applyId={id} />
          {/* LINEチャット */}
          {detailData.basicInfo?.lineId && (
            <div className={styles.field}>
              <ChatInputField
                detailData={detailData}
                chatInputFormik={chatInputFormik}
                freeChatInputFormik={freeChatInputFormik}
                isChatSubmitInProgress={isChatSubmitInProgress}
                isFreeChatSubmitInProgress={isFreeChatSubmitInProgress}
              />
            </div>
          )}
        </MaybeLoading>
      </div>
      {isBasicInformationInputChanged &&
        <AppBar className={styles.actionFooter} position="sticky" color="default">
          <Toolbar>
            <div className={styles.actionButtons}>
              <AsyncButton
                type='button'
                variant='contained'
                color='primary'
                onClick={async () => {
                  const errors = await basicInformationInputFormik.validateForm();
                  setShouldDisplayBasicInformationErrorMessage(true);
                  if (Object.keys(errors).length === 0) {
                    basicInformationInputFormik.handleSubmit();
                  } else {
                    // 一度もフォームをクリックしていないと、chatInputFormikのtouchedがtrueにならず、
                    // 入力欄が赤くならないので、ここでhandleSubmit()する。
                    // このelse句の中にはerrorがある時しか通らないので、送信はされない。
                    basicInformationInputFormik.handleSubmit();
                    scrollToBasicInformationInputErrorMessage();
                  }
                }}
                loading={isBasicInformationSubmitInProgress}
                disabled={!isBasicInformationInputChanged}
              >
                更新する
              </AsyncButton>
            </div>
          </Toolbar>
        </AppBar>
      }
    </div>
  );
};

export default Detail;
